import React from 'react';
import { Row, Col, Typography, Space, Badge, Input, InputNumber, Switch, Select, Tag } from 'antd';
import { SectionCard } from '../../../../components/shared';
import { SupplementCustomFields as SupplementCustomFieldsType, defaultSupplementFields } from '../../../../types/inventory/ProductTypes';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface SupplementCustomFieldsProps {
	customFields: SupplementCustomFieldsType;
	isEditing?: boolean;
	onFieldChange?: (fieldName: keyof SupplementCustomFieldsType, value: any) => void;
}

const SupplementCustomFields: React.FC<SupplementCustomFieldsProps> = ({ 
	customFields, 
	isEditing = false,
	onFieldChange
}) => {
	

	// Use provided customFields or defaults if empty/undefined
	const fields = customFields && Object.keys(customFields).length > 0 
		? { ...defaultSupplementFields, ...customFields }
		: defaultSupplementFields;

	const renderField = (label: string, fieldName: keyof SupplementCustomFieldsType, value: any, type: 'text' | 'textarea' | 'array' | 'boolean' | 'price' | 'select' = 'text', options?: string[]) => {
		// Only hide completely undefined/null values, but show empty strings and empty arrays
		if (value === undefined || value === null) return null;

		const handleFieldChange = (newValue: any) => {
			if (onFieldChange) {
				onFieldChange(fieldName, newValue);
			}
		};

		const renderValue = () => {
			if (isEditing) {
				switch (type) {
					case 'textarea':
						return (
							<TextArea
								value={value || ''}
								onChange={(e) => handleFieldChange(e.target.value)}
								rows={3}
								placeholder={`Enter ${label.toLowerCase()}`}
							/>
						);
					case 'array':
						return (
							<Select
								mode="tags"
								style={{ width: '100%' }}
								value={Array.isArray(value) ? value : []}
								onChange={handleFieldChange}
								placeholder={`Add ${label.toLowerCase()}`}
								options={options ? options.map(opt => ({ label: opt, value: opt })) : undefined}
							/>
						);
					case 'boolean':
						return (
							<Switch
								checked={Boolean(value)}
								onChange={handleFieldChange}
							/>
						);
					case 'price':
						return (
							<InputNumber
								style={{ width: '100%' }}
								prefix="$"
								precision={2}
								min={0}
								value={typeof value === 'number' ? value : 0}
								onChange={(val) => handleFieldChange(val || 0)}
								placeholder="0.00"
							/>
						);
					case 'select':
						return (
							<Select
								style={{ width: '100%' }}
								value={value || undefined}
								onChange={handleFieldChange}
								placeholder={`Select ${label.toLowerCase()}`}
								allowClear
								options={options ? options.map(opt => ({ label: opt, value: opt })) : []}
							/>
						);
					default:
						return (
							<Input
								value={value || ''}
								onChange={(e) => handleFieldChange(e.target.value)}
								placeholder={`Enter ${label.toLowerCase()}`}
							/>
						);
				}
			} else {
				// Read-only mode
				switch (type) {
					case 'array':
						return Array.isArray(value) && value.length > 0 ? (
							<Space wrap>
								{value.map((item, index) => (
									<Tag key={index} color="blue">{item}</Tag>
								))}
							</Space>
						) : <Text type="secondary" italic>None specified</Text>;
					
					case 'boolean':
						return (
							<Badge 
								status={value ? "success" : "default"} 
								text={value ? "Yes" : "No"} 
							/>
						);
					
					case 'price':
						return <Text strong>${typeof value === 'number' ? value.toFixed(2) : value}</Text>;
					
					default:
						return value && value !== '' ? <Text>{value}</Text> : <Text type="secondary" italic>Not specified</Text>;
				}
			}
		};

		return (
			<div style={{ marginBottom: 12 }}>
				<Text strong style={{ display: 'block', marginBottom: 4 }}>
					{label}:
				</Text>
				{renderValue()}
			</div>
		);
	};

	const renderSection = (title: string, children: React.ReactNode) => {
		return (
			<div style={{ marginBottom: 24 }}>
				<Title level={5} style={{ color: 'var(--mg-primary)', marginBottom: 16 }}>
					{title}
				</Title>
				{children}
			</div>
		);
	};

	return (
		<SectionCard 
			title={
				<Space>
					<Title level={4} style={{ margin: 0 }}>
						Supplement Information
					</Title>
					{fields.is_active !== undefined && (
						<Badge 
							status={fields.is_active ? "processing" : "default"} 
							text={fields.is_active ? "Active" : "Inactive"}
						/>
					)}
				</Space>
			}
			style={{ marginTop: 16 }}
		>
			<Row gutter={[24, 0]}>
				<Col xs={24} lg={12}>
					{renderSection("Basic Information", (
						<>
							{renderField("Display Name", "display_name", fields.display_name)}
							{renderField("Brand", "brand", fields.brand)}
							{renderField("Vendor", "vendor", fields.vendor)}
							{renderField("Form", "form", fields.form)}
							{renderField("Sex Specification", "sex", fields.sex, 'select', ['male', 'female', 'unisex'])}
							{renderField("Vegan", "is_vegan", fields.is_vegan, 'boolean')}
						</>
					))}

					{renderSection("Dosage & Timing", (
						<>
							{renderField("Dosage", "dosage", fields.dosage, 'textarea')}
							{renderField("Frequency", "frequency", fields.frequency)}
							{renderField("Timing Preference", "timing_preference", fields.timing_preference, 'select', ['morning', 'afternoon', 'evening', 'bedtime', 'with meals', 'between meals'])}
						</>
					))}

					{renderSection("Pricing", (
						<>
							{renderField("Retail Price", "price", fields.price, 'price')}
							{renderField("Wholesale Price", "wholesale_price", fields.wholesale_price, 'price')}
						</>
					))}
				</Col>

				<Col xs={24} lg={12}>
					{renderSection("Clinical Information", (
						<>
							{renderField("Mechanism of Action", "mechanism_of_action", fields.mechanism_of_action, 'textarea')}
							{renderField("Genetic Markers", "genetic_markers", fields.genetic_markers, 'array', ['MTHFR', 'COMT', 'APOE', 'FUT2', 'GSTM1', 'GSTP1', 'NAT2', 'CYP1A2', 'CYP2D6', 'ALDH2'])}
							{renderField("Indications", "indications", fields.indications, 'array')}
							{renderField("Contraindications", "contraindications", fields.contraindications, 'array')}
							{renderField("Interactions", "interactions", fields.interactions, 'array')}
						</>
					))}

					{renderSection("Additional Information", (
						<>
							{renderField("Manufacturing Notes", "manufacturing_notes", fields.manufacturing_notes, 'textarea')}
							{renderField("Reference Link", "link", fields.link)}
							{renderField("Active Status", "is_active", fields.is_active, 'boolean')}
						</>
					))}
				</Col>
			</Row>
		</SectionCard>
	);
};

export default SupplementCustomFields;